"use client";

import { motion } from "framer-motion";
import { Autoplay, EffectCoverflow } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import Image from "next/image";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css";
import "swiper/css/effect-cards";

import Link from "next/link";
import { Button } from "@/components/ui/button";

// Real examples with actual API outputs - diverse, visually appealing images
const heroExamples = [
  {
    src: "/images/robbery_example.jpeg",
    alt: "Security monitoring",
    query: "what is happening?",
    response: "a man is trying to break into a white car",
  },
  {
    src: "/images/usecases/brand_detection_large.jpeg",
    alt: "Brand detection",
    query: "what brands are visible?",
    response: "Coca-Cola",
  },
  {
    src: "/images/solutions/generate-media-tags/categorize.png",
    alt: "Category classification",
    query: "category: dining out or groceries?",
    response: "dining out",
  },
  {
    src: "/images/usecases/summarization_example_large.jpeg",
    alt: "Wildlife observation",
    query: "describe what you see",
    response: "Two cheetahs lying in dry grass",
  },
  {
    src: "/images/solutions/retail-analytics/retail_intelligence.jpg",
    alt: "Retail analytics",
    query: "workers and customers as json",
    response: '{ "workers": 1, "customers": 5 }',
    objectPosition: "right",
  },
];

// Duplicate for smooth looping with coverflow effect
const images = [...heroExamples, ...heroExamples];

const css = `
  .mySwiperHero210 {
    width: 100%;
    height: 480px;
    padding-bottom: 50px;
  }
  
  .mySwiperHero210 .swiper-slide {
    background: transparent;
    width: 520px;
    margin: 0 20px;
  }
  
  .mySwiperHero210 .swiper-slide img {
    display: block;
    width: 100%;
  }
  
  .swiper-3d .swiper-slide-shadow-left,
  .swiper-3d .swiper-slide-shadow-right {
    display: none !important;
    background: none !important;
    background-image: none !important;
  }
  
`;

export default function QueryHero() {
  return (
    <section className="pt-8 sm:pt-20">
      <style>{css}</style>
      <div className="container mx-auto px-0 sm:px-8">
        <div className="mx-auto flex max-w-3xl flex-col items-center justify-center gap-4 px-8 sm:px-0">
          <h1
            className="text-center text-5xl lg:text-7xl tracking-[-0.01em]"
            style={{ fontFamily: 'var(--font-instrument-serif)' }}
          >
            Vision AI that can answer questions at the speed of sight
          </h1>
          <p className="text-muted-foreground text-center lg:text-lg">
            Moondream Query answers natural language questions about images with detailed, accurate responses. From OCR to scene understanding, get the answers you need in milliseconds.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg" className="rounded-full px-6 py-3">
              <Link href="/c/playground?skill=query">Try Query</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="rounded-full px-6 py-3"
            >
              <a
                href="https://docs.moondream.ai/skills/query"
                target="_blank"
                rel="noreferrer"
              >
                View docs
              </a>
            </Button>
          </div>
        </div>

        <div className="relative mt-12 h-[480px] w-full lg:px-20">
          <div className="absolute left-0 z-10 h-full w-16 md:w-24 bg-gradient-to-r from-background to-transparent lg:left-20" />
          <div className="absolute right-0 z-10 h-full w-16 md:w-24 bg-gradient-to-l from-background to-transparent lg:right-20" />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="w-full"
          >
            <Swiper
              autoplay={{
                delay: 6000,
                disableOnInteraction: false,
              }}
              effect="coverflow"
              grabCursor
              slidesPerView="auto"
              centeredSlides
              loop
              coverflowEffect={{
                rotate: 50,
                stretch: 0,
                depth: 100,
                modifier: 1,
                slideShadows: false,
              }}
              className="mySwiperHero210"
              modules={[EffectCoverflow, Autoplay]}
            >
              {images.map((example, index) => (
                <SwiperSlide key={index} className="!bg-transparent">
                  <div className="relative h-full w-full overflow-hidden rounded-3xl shadow-lg">
                    <Image
                      className="h-full w-full object-cover"
                      style={{ objectPosition: example.objectPosition || "center" }}
                      src={example.src}
                      alt={example.alt}
                      width={520}
                      height={480}
                    />
                    
                    {/* Query/Response overlays */}
                    <div className="absolute bottom-4 left-4 right-4 flex flex-col gap-2.5">
                      {/* Query container */}
                      <div className="bg-white/10 backdrop-blur-md rounded-lg border border-white/20 px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="text-purple-300 text-xs font-semibold uppercase tracking-wider shrink-0">Q</span>
                          <code className="text-white/90 text-base font-mono truncate">
                            &quot;{example.query}&quot;
                          </code>
                        </div>
                      </div>

                      {/* Response container */}
                      <div className="bg-white/15 backdrop-blur-md rounded-lg border border-white/25 px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="text-green-300 text-xs font-semibold uppercase tracking-wider shrink-0">→</span>
                          <code className="text-white font-medium text-base font-mono leading-relaxed line-clamp-2">
                            {example.response}
                          </code>
                        </div>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>
        </div>
      </div>
    </section >
  );
}

